import { BOOKING_URL } from "../../models/site";
import { useHomeVideoPresenter } from "../../presenters/useHomeVideoPresenter";
import { HomeEventsSlider } from "../events/HomeEventsSlider";
import { SmartImage } from "../SmartImage";

export type HomeVideoPlacement = "hero" | "image-band";

type HomePageProps = {
  videoPlacement?: HomeVideoPlacement;
};

function HomePreviewVideo({
  className,
  poster,
  adaptive = false,
}: {
  className: string;
  poster: string;
  adaptive?: boolean;
}) {
  const { videoRef, hasPlayed } = useHomeVideoPresenter(adaptive);
  return (
    <>
      <video
        ref={videoRef}
        className={className}
        muted
        loop
        playsInline
        preload="none"
        poster={poster}
        aria-hidden="true"
        data-playing={hasPlayed}
      />
      <a
        className="video-credit"
        hidden={!hasPlayed}
        href="https://www.instagram.com/chandler_borries"
        target="_blank"
        rel="noreferrer"
      >
        Film by <span>@chandler_borries</span>
      </a>
    </>
  );
}

export function HomePage({ videoPlacement }: HomePageProps = {}) {
  const showsHeroVideo = videoPlacement === "hero";
  const showsImageBandVideo = videoPlacement === "image-band";

  return (
    <>
      <section className="hero home-hero">
        <SmartImage
          name="hero"
          alt="藍天下的都蘭小星民宿老屋"
          className={showsHeroVideo ? "home-hero-fallback" : undefined}
          eager
          width={2000}
          height={1333}
          sizes="100vw"
        />
        {showsHeroVideo ? (
          <HomePreviewVideo
            className="home-hero-video"
            poster="/images/hero-1280.jpg"
          />
        ) : null}
        <div className="hero-copy fade-in">
          <p>A Simple Place to Rest & Connect.</p>
          <h1>
            都蘭小星民宿<span>DULAN STAR GUEST HOUSE</span>
          </h1>
          <a
            className="hero-button"
            href={BOOKING_URL}
            target="_blank"
            rel="noreferrer"
          >
            BOOK YOUR STAY
          </a>
        </div>
      </section>
      <section className="home-story split-section">
        <div>
          <p className="eyebrow">WELCOME TO DULAN</p>
          <h2>
            在山與海之間，
            <br />
            留一個自在的位置給你。
          </h2>
        </div>
        <div className="prose">
          <p>
            小星座落在台東都蘭，一棟保留時間痕跡的老屋。這裡沒有制式旅程，只有舒服的床、溫暖的公共空間，以及隨時可以走向山海的日常。
          </p>
          <p>
            我們相信旅行不只是抵達一個地方，也是在不同的人與生活之間，找到真實的連結。
          </p>
          <p>
            Between the mountains and the Pacific, Dulan Star is a simple place
            to slow down, rest well and connect with local life.
          </p>
          <a className="text-link" href="/rooms">
            EXPLORE OUR ROOMS →
          </a>
        </div>
      </section>
      <section
        className={
          showsImageBandVideo ? "image-band image-band-with-video" : "image-band"
        }
      >
        <SmartImage
          name="courtyard"
          alt="都蘭小星民宿庭院"
          className={showsImageBandVideo ? "image-band-fallback" : undefined}
          sizes="100vw"
        />
        {showsImageBandVideo ? (
          <HomePreviewVideo
            className="image-band-video"
            poster="/images/courtyard-1280.jpg"
            adaptive
          />
        ) : null}
      </section>
      <HomeEventsSlider />
    </>
  );
}
